import "./TableUserInfoItem.scss";

import React, { memo, useMemo } from "react";

import { Avatar, Tag, Typography } from "antd";
import PropTypes from "prop-types";
import { IoCopyOutline } from "react-icons/io5";

import { useAppTheme } from "../../context/ThemeProvider";
import { DEFAULT_AVATAR, INDIA_NAMES } from "../../Utils/constants";
import {
  copyToClipboard,
  getCallingCode,
  getCountryByCode,
} from "../../Utils/utilityFunctions";

const copyIconStyle = {
  cursor: "pointer",
};

const titleStyle = {
  maxWidth: 150,
  // textOverflow: "ellipsis",
  margin: 0,
};

const countryItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "max-content",
  marginTop: 8,
};

const countryTagTextStyle = {
  margin: 0,
  color: "var(--icon)",
};

const ContactInfoTableItem = ({
  name,
  profilePic,
  phone,
  email,
  host,
  country,
  dialCode,
  isCopyable = true,
  hideContact = false,
  hideProfilePic = false,
  hideHost = false,
}) => {
  const { COLORS } = useAppTheme();

  const phoneNumberWithCountryCode = useMemo(() => {
    if (dialCode) {
      return `${dialCode}-${phone}`;
    }

    return country ? `${getCallingCode(country)}-${phone}` : phone;
  }, [country, phone, dialCode]);

  const userCountry = useMemo(
    () => (country ? getCountryByCode(country?.toUpperCase()) : null),
    [country]
  );

  if (!name && !phone && !email) return "-";

  return (
    <>
      {name ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hideProfilePic ? null : (
            <Avatar
              src={profilePic || DEFAULT_AVATAR}
              size={36}
              style={{
                flexShrink: 0,
              }}
            />
          )}
          <Typography.Paragraph
            style={titleStyle}
            ellipsis={{ rows: 1, tooltip: true }}
          >
            {name || "DELETED USER"}
          </Typography.Paragraph>
        </div>
      ) : null}

      {hideContact ? null : (
        <div
          style={{
            marginLeft: 40,
          }}
        >
          {phone ? (
            <div className="copyable-link">
              <a href={`tel:${phoneNumberWithCountryCode}`}>
                {phoneNumberWithCountryCode}
              </a>{" "}
              {isCopyable && (
                <IoCopyOutline
                  color={COLORS.ICON}
                  style={copyIconStyle}
                  onClick={() => copyToClipboard(String(phone))}
                />
              )}
            </div>
          ) : (
            "-"
          )}

          {email ? (
            <div className="copyable-link">
              <a href={`mailto:${email}`}>{email}</a>{" "}
              {isCopyable && (
                <IoCopyOutline
                  color={COLORS.ICON}
                  style={copyIconStyle}
                  onClick={() => copyToClipboard(email)}
                />
              )}
            </div>
          ) : (
            "-"
          )}

          {!hideHost && host ? (
            <div className="copyable-link">
              <span>
                Host: <a href={host}>{host}</a>
              </span>{" "}
              {isCopyable && (
                <IoCopyOutline
                  color={COLORS.ICON}
                  style={copyIconStyle}
                  onClick={() => copyToClipboard(host)}
                />
              )}
            </div>
          ) : null}

          {userCountry ? (
            <Tag style={countryItemStyle}>
              {userCountry?.flag ? (
                <img
                  src={userCountry?.flag}
                  alt={country?.toUpperCase()}
                  width={16}
                />
              ) : null}
              <span style={countryTagTextStyle}>
                {INDIA_NAMES.includes(country)
                  ? "INDIA"
                  : userCountry?.name?.common?.toUpperCase()}
              </span>
            </Tag>
          ) : null}
        </div>
      )}
    </>
  );
};

ContactInfoTableItem.propTypes = {
  name: PropTypes.string,
  profilePic: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  host: PropTypes.string,
  country: PropTypes.string,
  dialCode: PropTypes.string,
  isCopyable: PropTypes.bool,
  hideContact: PropTypes.bool,
  hideProfilePic: PropTypes.bool,
  hideHost: PropTypes.bool,
};

export default memo(ContactInfoTableItem);
